import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from './AdminLayout';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
function adminHeaders() {
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('hrv_admin_token')}` };
}

const EMPTY_FORM = {
  test_name: '', condition: 'high', severity: 'moderate',
  interpretation: '', dietary_advice: '', lifestyle_advice: '',
  followup_tests: '', see_doctor_if: '',
};

function InterpModal({ initial, onSave, onClose }) {
  const [form, setForm]     = useState({ ...EMPTY_FORM, ...initial });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.test_name.trim()) { setError('Test name is required'); return; }
    setSaving(true); setError('');
    try {
      await onSave(form);
      onClose();
    } catch (e) {
      setError(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const Field = ({ label, name, type = 'text', rows }) => (
    <div style={{ marginBottom: '16px' }}>
      <label style={{ display: 'block', fontWeight: '600', fontSize: '0.8rem', color: '#4a5568', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {label}
      </label>
      {rows ? (
        <textarea value={form[name]} onChange={e => set(name, e.target.value)} rows={rows}
          style={{ width: '100%', padding: '10px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.875rem', resize: 'vertical', boxSizing: 'border-box' }} />
      ) : type === 'select' ? null : (
        <input type={type} value={form[name]} onChange={e => set(name, e.target.value)}
          style={{ width: '100%', padding: '10px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.875rem', boxSizing: 'border-box' }} />
      )}
    </div>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 300, padding: '16px' }}>
      <div style={{ background: 'white', borderRadius: '14px', width: '100%', maxWidth: '620px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 25px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ padding: '24px 28px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>
          <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700' }}>{initial?.id ? 'Edit' : 'Add'} Interpretation</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#718096' }}>×</button>
        </div>
        <div style={{ padding: '24px 28px' }}>
          {error && <div style={{ background: '#fff5f5', border: '1px solid #fed7d7', color: '#c53030', padding: '10px', borderRadius: '8px', marginBottom: '16px', fontSize: '0.875rem' }}>{error}</div>}

          <Field label="Test Name *" name="test_name" />

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
            <div>
              <label style={{ display: 'block', fontWeight: '600', fontSize: '0.8rem', color: '#4a5568', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Condition *</label>
              <select value={form.condition} onChange={e => set('condition', e.target.value)}
                style={{ width: '100%', padding: '10px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.875rem', background: 'white' }}>
                <option value="high">High</option>
                <option value="low">Low</option>
                <option value="positive">Positive</option>
                <option value="abnormal">Abnormal</option>
                <option value="normal">Normal</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontWeight: '600', fontSize: '0.8rem', color: '#4a5568', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Severity</label>
              <select value={form.severity} onChange={e => set('severity', e.target.value)}
                style={{ width: '100%', padding: '10px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.875rem', background: 'white' }}>
                <option value="">— select —</option>
                <option value="mild">Mild</option>
                <option value="moderate">Moderate</option>
                <option value="severe">Severe</option>
                <option value="critical">Critical</option>
              </select>
            </div>
          </div>

          <Field label="Interpretation" name="interpretation" rows={3} />
          <Field label="Dietary Advice" name="dietary_advice" rows={3} />
          <Field label="Lifestyle Advice" name="lifestyle_advice" rows={3} />
          <Field label="Follow-up Tests" name="followup_tests" rows={2} />
          <Field label="See Doctor If" name="see_doctor_if" rows={2} />

          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '8px' }}>
            <button onClick={onClose} style={{ padding: '10px 20px', background: '#edf2f7', color: '#4a5568', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '500' }}>Cancel</button>
            <button onClick={handleSave} disabled={saving} style={{ padding: '10px 24px', background: '#6366f1', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const CONDITION_COLORS = {
  high:     { bg: '#fff5f5', color: '#c53030' },
  low:      { bg: '#ebf8ff', color: '#2b6cb0' },
  positive: { bg: '#fff5f5', color: '#c05621' },
  abnormal: { bg: '#faf5ff', color: '#553c9a' },
  normal:   { bg: '#f0fff4', color: '#276749' },
};

const SEVERITY_COLORS = {
  mild:     '#48bb78',
  moderate: '#ed8936',
  severe:   '#e53e3e',
  critical: '#9b2335',
};

export default function AdminInterpretations() {
  const [items, setItems]     = useState([]);
  const [total, setTotal]     = useState(0);
  const [gaps, setGaps]       = useState([]);
  const [search, setSearch]   = useState('');
  const [page, setPage]       = useState(1);
  const [modal, setModal]     = useState(null);  // null | {} | {id, ...}
  const [deleting, setDeleting] = useState(null);
  const adminInfo = JSON.parse(localStorage.getItem('hrv_admin_info') || '{}');
  const canEdit   = ['super_admin', 'trusted_admin'].includes(adminInfo.role);
  const canDelete = adminInfo.role === 'super_admin';
  const limit = 50;

  const fetchItems = useCallback(() => {
    const params = new URLSearchParams({ page, limit, search }).toString();
    fetch(`${API_URL}/admin/interpretations?${params}`, { headers: adminHeaders() })
      .then(r => r.json()).then(d => { setItems(d.interpretations || []); setTotal(d.total || 0); });
  }, [page, search]);

  const fetchGaps = useCallback(() => {
    fetch(`${API_URL}/admin/interpretations/gaps`, { headers: adminHeaders() })
      .then(r => r.json()).then(d => setGaps(d.gaps || []));
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);
  useEffect(() => { fetchGaps(); }, [fetchGaps]);

  const handleSave = async (form) => {
    const url = form.id ? `${API_URL}/admin/interpretations/${form.id}` : `${API_URL}/admin/interpretations`;
    const method = form.id ? 'PUT' : 'POST';
    const res = await fetch(url, { method, headers: adminHeaders(), body: JSON.stringify(form) });
    if (!res.ok) { const d = await res.json(); throw new Error(d.error || 'Save failed'); }
    fetchItems(); fetchGaps();
  };

  const handleDelete = async (id) => {
    if (deleting !== id) { setDeleting(id); return; }
    await fetch(`${API_URL}/admin/interpretations/${id}`, { method: 'DELETE', headers: adminHeaders() });
    setDeleting(null); fetchItems(); fetchGaps();
  };

  return (
    <AdminLayout>
      {modal !== null && (
        <InterpModal initial={modal} onSave={handleSave} onClose={() => setModal(null)} />
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontSize: '1.6rem', fontWeight: '700', color: '#1a202c', margin: 0 }}>Medical Interpretations</h1>
          <p style={{ color: '#718096', margin: '6px 0 0' }}>{total} entries in the database</p>
        </div>
        {canEdit && (
          <button onClick={() => setModal({})} style={{
            padding: '10px 20px', background: '#6366f1', color: 'white', border: 'none',
            borderRadius: '8px', fontWeight: '600', fontSize: '0.9rem', cursor: 'pointer',
          }}>
            + Add Interpretation
          </button>
        )}
      </div>

      {/* Search */}
      <input value={search} placeholder="Search by test name…"
        onChange={e => { setSearch(e.target.value); setPage(1); }}
        style={{ width: '100%', maxWidth: '400px', padding: '10px 14px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '0.9rem', marginBottom: '20px', outline: 'none', boxSizing: 'border-box' }}
      />

      {/* Table */}
      <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', overflow: 'hidden', marginBottom: '32px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f7fafc', borderBottom: '2px solid #e2e8f0' }}>
              {['Test Name', 'Condition', 'Severity', 'Has Content', 'Actions'].map(h => (
                <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: '0.78rem', fontWeight: '700', color: '#718096', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={5} style={{ padding: '40px', textAlign: 'center', color: '#a0aec0' }}>No interpretations found</td></tr>
            ) : items.map((item, i) => {
              const cs = CONDITION_COLORS[item.condition] || { bg: '#f7fafc', color: '#4a5568' };
              return (
                <tr key={item.id} style={{ borderBottom: '1px solid #f0f4f8', background: i % 2 === 0 ? 'white' : '#fafafa' }}>
                  <td style={{ padding: '12px 16px', fontWeight: '600', color: '#2d3748', fontSize: '0.9rem' }}>{item.test_name}</td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '600', background: cs.bg, color: cs.color }}>
                      {item.condition}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    {item.severity && (
                      <span style={{ display: 'inline-block', width: '10px', height: '10px', borderRadius: '50%', background: SEVERITY_COLORS[item.severity] || '#718096', marginRight: '6px' }} />
                    )}
                    <span style={{ color: '#4a5568', fontSize: '0.875rem' }}>{item.severity || '—'}</span>
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    {['interpretation', 'dietary_advice', 'lifestyle_advice'].map(f => (
                      <span key={f} title={f.replace('_', ' ')} style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', background: item[f] ? '#48bb78' : '#e2e8f0', marginRight: '4px' }} />
                    ))}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {canEdit && (
                        <button onClick={() => setModal(item)} style={{ padding: '5px 12px', background: '#edf2f7', color: '#4a5568', border: 'none', borderRadius: '6px', fontSize: '0.8rem', cursor: 'pointer', fontWeight: '500' }}>Edit</button>
                      )}
                      {canDelete && (
                        <button onClick={() => handleDelete(item.id)} style={{
                          padding: '5px 12px', background: deleting === item.id ? '#c53030' : '#fff5f5',
                          color: deleting === item.id ? 'white' : '#c53030', border: '1px solid #fed7d7',
                          borderRadius: '6px', fontSize: '0.8rem', cursor: 'pointer', fontWeight: '500',
                        }}>
                          {deleting === item.id ? 'Confirm?' : 'Delete'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {Math.ceil(total / limit) > 1 && (
          <div style={{ padding: '14px', display: 'flex', justifyContent: 'center', gap: '8px', borderTop: '1px solid #e2e8f0' }}>
            <button onClick={() => setPage(p => Math.max(p - 1, 1))} disabled={page <= 1}
              style={{ padding: '6px 14px', borderRadius: '6px', border: '1px solid #e2e8f0', background: 'white', cursor: page <= 1 ? 'not-allowed' : 'pointer', color: '#4a5568' }}>← Prev</button>
            <span style={{ padding: '6px 14px', color: '#718096', fontSize: '0.875rem' }}>Page {page} of {Math.ceil(total / limit)}</span>
            <button onClick={() => setPage(p => Math.min(p + 1, Math.ceil(total / limit)))} disabled={page >= Math.ceil(total / limit)}
              style={{ padding: '6px 14px', borderRadius: '6px', border: '1px solid #e2e8f0', background: 'white', cursor: 'pointer', color: '#4a5568' }}>Next →</button>
          </div>
        )}
      </div>

      {/* Coverage Gaps */}
      {gaps.length > 0 && (
        <div>
          <h2 style={{ fontSize: '1.15rem', fontWeight: '700', color: '#1a202c', margin: '0 0 16px' }}>
            Coverage Gaps ({gaps.length})
          </h2>
          <p style={{ color: '#718096', fontSize: '0.875rem', margin: '-8px 0 16px' }}>Tests with High but missing Low, or vice versa</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '12px' }}>
            {gaps.map(gap => (
              <div key={gap.test_name} style={{ background: 'white', borderRadius: '10px', padding: '16px 18px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0' }}>
                <div style={{ fontWeight: '600', color: '#2d3748', marginBottom: '10px', fontSize: '0.9rem' }}>{gap.test_name}</div>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '0.78rem', padding: '2px 8px', borderRadius: '12px', background: gap.has_high ? '#f0fff4' : '#fff5f5', color: gap.has_high ? '#276749' : '#c53030', border: `1px solid ${gap.has_high ? '#9ae6b4' : '#fed7d7'}` }}>
                    {gap.has_high ? '✓ Has High' : '✗ Missing High'}
                  </span>
                  <span style={{ fontSize: '0.78rem', padding: '2px 8px', borderRadius: '12px', background: gap.has_low ? '#f0fff4' : '#fff5f5', color: gap.has_low ? '#276749' : '#c53030', border: `1px solid ${gap.has_low ? '#9ae6b4' : '#fed7d7'}` }}>
                    {gap.has_low ? '✓ Has Low' : '✗ Missing Low'}
                  </span>
                </div>
                {canEdit && (
                  <button
                    onClick={() => setModal({ test_name: gap.test_name, condition: gap.has_high ? 'low' : 'high', severity: 'moderate', interpretation: '', dietary_advice: '', lifestyle_advice: '', followup_tests: '', see_doctor_if: '' })}
                    style={{ fontSize: '0.8rem', padding: '5px 12px', background: '#6366f1', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '500' }}>
                    + Add {gap.has_high ? 'Low' : 'High'}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
